import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { useEstateContext } from "@/lib/EstateContext";
import EstateSwitcher from "@/components/EstateSwitcher";
import { PresenceAvatars } from "@/components/PresenceAvatars";
import { useEstatePresence } from "@/hooks/useEstatePresence";
import { supabase } from "@/lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { 
  Map, 
  Plus, 
  LayoutDashboard, 
  FileText, 
  Briefcase,
  Menu,
  User,
  LogOut,
  Bell,
  Settings,
  Activity,
  Trophy,
  Building2
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Onboarding from "@/components/Onboarding";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { motion } from "framer-motion";
import darkLogo from "@/assets/Dark_mode_red_large.png";
import lightLogo from "@/assets/Light_mode_large_logo.png";

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoadingAuth: isLoading, logout, isAuthSettled } = useAuth();
  const estateCtx = useEstateContext();
  const mobilePresence = useEstatePresence(estateCtx?.activeEstateId, user?.id);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme') === 'dark' || 
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    // Only check onboarding if user is loaded and is a resident
    if (!user) return;
    
    // Only show onboarding for residents
    if (user.user_role === 'resident') {
      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      if (!onboardingCompleted) {
        setShowOnboarding(true);
      }
    }
  }, [user]);

  useEffect(() => {
    // Wait for a real DB read before acting on user_role.
    // During OAuth redirect the DB can be slow; we temporarily set user with a
    // null role from localStorage cache while the real query is still in flight.
    // isAuthSettled becomes true only after loadUser gets a successful DB response,
    // so we never send an existing user to RoleSetup due to a slow first query.
    if (!user || !isAuthSettled) return;
    
    if (!user.user_role) {
      if (location.pathname.toLowerCase() !== '/rolesetup') {
        navigate('/RoleSetup');
      }
    }
  }, [user, isAuthSettled, location.pathname, navigate]);

  // Redirect estate managers with no estates to CreateEstate
  useEffect(() => {
    if (!isAuthSettled || !user || user.user_role !== 'estate_manager') return;
    // Use lowercase comparison — actual paths are PascalCase but createPageUrl lowercases
    const currentPath = location.pathname.toLowerCase();
    if (currentPath === '/createestate') return;
    if (currentPath.includes('joinestate')) return;

    // Only redirect once estates have fully loaded from the join table FOR the current user.
    // Check loadedForUserId === user.id to prevent false redirects during the one-render
    // window where auth settles but EstateContext hasn't yet started fetching for this user.
    const estatesLoaded = estateCtx &&
      !estateCtx.isLoadingEstates &&
      estateCtx.loadedForUserId === user.id;
    if (estatesLoaded && estateCtx.myEstates.length === 0) {
      console.log('🏢 Estate manager with no estates, redirecting to CreateEstate...');
      navigate('/CreateEstate');
    }
    // If EstateContext hasn't mounted yet, do nothing — wait for it to load.
  }, [isAuthSettled, user, location.pathname, navigate, estateCtx?.myEstates, estateCtx?.isLoadingEstates]);

  // Listen for theme changes
  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme');
      const isDark = theme === 'dark' || document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };
    
    // Check on mount
    checkTheme();
    
    // Listen for changes to localStorage and DOM
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    window.addEventListener('storage', checkTheme);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('storage', checkTheme);
    };
  }, []);

  const handleLogout = async () => {
    // Use the same path as Settings — await logout() calls window.location.replace('/Login')
    // which does a full page reload, bypassing any React re-render issues entirely.
    await logout();
  };

  const residentNav = [
    { title: "Map View", url: createPageUrl("MapView"), icon: Map },
    { title: "Report Issue", url: createPageUrl("ReportIssue"), icon: Plus },
    { title: "My Reports", url: createPageUrl("MyReports"), icon: FileText },
    { title: "My Activity", url: createPageUrl("MyActivity"), icon: Activity },
    { title: "Leaderboard", url: createPageUrl("Leaderboard"), icon: Trophy },
  ];

  const cityWorkerNav = [
    { title: "Map View", url: createPageUrl("MapView"), icon: Map },
    { title: "Dashboard", url: createPageUrl("CityDashboard"), icon: LayoutDashboard },
    { title: "All Reports", url: createPageUrl("MyReports"), icon: FileText },
    { title: "Leaderboard", url: createPageUrl("Leaderboard"), icon: Trophy },
  ];

  const contractorNav = [
    { title: "Available Jobs", url: createPageUrl("ContractorJobs"), icon: Briefcase },
    { title: "My Tasks", url: createPageUrl("MyTasks"), icon: FileText },
    { title: "Map View", url: createPageUrl("MapView"), icon: Map },
    { title: "Leaderboard", url: createPageUrl("Leaderboard"), icon: Trophy },
  ];

  const estateManagerNav = [
    { title: "Dashboard", url: createPageUrl("EstateDashboard"), icon: LayoutDashboard },
    // Hidden for now - will add back when COCT integration is ready
    // { title: "Map View", url: createPageUrl("MapView"), icon: Map },
    // { title: "Report Issue", url: createPageUrl("ReportIssue"), icon: Plus },
  ];

  const getNavItems = () => {
    if (!user) return residentNav;
    switch (user.user_role) {
      case "city_worker":
        return cityWorkerNav;
      case "contractor":
        return contractorNav;
      case "estate_manager":
        return estateManagerNav;
      default:
        return residentNav;
    }
  };

  const navigationItems = getNavItems();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0F0F0' }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#1883FF', borderTopColor: 'transparent' }} />
          <p className="text-gray-700 font-medium">Loading FixMyHood...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        :root {
          --color-primary: 20 184 166;
          --color-primary-dark: 13 148 136;
          --color-secondary: 16 185 129;
          --color-accent: 6 182 212;
          --color-surface: 248 250 252;
          --color-background: 241 245 249;
          
          --shadow-xs: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-sm: 0 2px 8px -2px rgb(0 0 0 / 0.08);
          --shadow-md: 0 4px 16px -4px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 8px 24px -6px rgb(0 0 0 / 0.12);
          --shadow-xl: 0 12px 32px -8px rgb(0 0 0 / 0.15);
        }

        body {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Smooth transitions */
        * {
          transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform, filter;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 200ms;
        }

        /* Enhanced hover effects */
        button:hover:not(:disabled), 
        a:hover,
        [role="button"]:hover {
          transform: translateY(-1px);
        }

        button:active:not(:disabled), 
        a:active,
        [role="button"]:active {
          transform: translateY(0);
        }

        /* Card hover effects */
        .card-hover:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-xl);
        }

        /* Smooth scroll */
        html {
          scroll-behavior: smooth;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }

        ::-webkit-scrollbar-track {
          background: rgb(241 245 249);
          border-radius: 8px;
        }

        ::-webkit-scrollbar-thumb {
          background: rgb(148 163 184);
          border-radius: 8px;
          border: 2px solid rgb(241 245 249);
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgb(100 116 139);
        }

        /* Dark mode scrollbar */
        .dark ::-webkit-scrollbar-track {
          background: rgb(15 23 42);
        }

        .dark ::-webkit-scrollbar-thumb {
          background: rgb(71 85 105);
          border-color: rgb(15 23 42);
        }

        .dark ::-webkit-scrollbar-thumb:hover {
          background: rgb(100 116 139);
        }

        /* Focus styles */
        *:focus-visible {
          outline: 2px solid rgb(20 184 166);
          outline-offset: 2px;
          border-radius: 8px;
        }

        /* Animations */
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }

        .animate-slide-in {
          animation: slide-in-right 0.4s ease-out;
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }

        /* Loading shimmer effect */
        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        .animate-shimmer {
          background: linear-gradient(
            90deg,
            #f1f5f9 0%,
            #e2e8f0 20%,
            #f1f5f9 40%,
            #f1f5f9 100%
          );
          background-size: 1000px 100%;
          animation: shimmer 2s linear infinite;
        }

        @media print {
          ::-webkit-scrollbar { display: none !important; }
          main { overflow: visible !important; }
          .overflow-auto { overflow: visible !important; }
          .overflow-hidden { overflow: visible !important; }
        }
      `}</style>

      <Toaster 
        position="top-center" 
        richColors 
        closeButton
        toastOptions={{
          classNames: {
            toast: 'bg-white dark:bg-white border border-slate-200 dark:border-slate-200 rounded-2xl shadow-lg',
            title: 'text-slate-900 dark:text-slate-900 font-semibold',
            description: 'text-slate-600 dark:text-slate-600',
            actionButton: 'bg-[#1883FF] text-white hover:bg-[#0066CC] rounded-xl',
            cancelButton: 'bg-slate-200 dark:bg-slate-200 text-slate-900 dark:text-slate-900 rounded-xl hover:bg-slate-300 dark:hover:bg-slate-300',
            closeButton: 'bg-white dark:bg-white border-slate-200 dark:border-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-50 text-slate-900 dark:text-slate-900',
            success: 'bg-white dark:bg-white border-green-200 dark:border-green-200',
            error: 'bg-white dark:bg-white border-red-200 dark:border-red-200',
            warning: 'bg-white dark:bg-white border-yellow-200 dark:border-yellow-200',
            info: 'bg-white dark:bg-white border-blue-200 dark:border-blue-200',
          },
        }}
      />
      
      <Onboarding 
        isOpen={showOnboarding} 
        onComplete={() => setShowOnboarding(false)} 
      />
      
      {/* Auth pages without sidebar */}
      {['/login', '/signup', '/forgotpassword', '/rolesetup', '/createestate'].includes(location.pathname.toLowerCase()) ? (
        <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full"
          >
            {children}
          </motion.div>
        </div>
      ) : (
        /* All other pages with sidebar */
        <SidebarProvider>
          <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900">
            <div className="print:hidden">
            <Sidebar className="border-r border-teal-100/50 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl">
            <SidebarHeader className="border-b border-teal-100/50 dark:border-slate-800 p-6">
              <Link to="/" className="flex items-center justify-center group">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-24 h-24 rounded-2xl flex items-center justify-center transition-all duration-300"
                >
                  <img 
                    src={isDarkMode ? darkLogo : lightLogo} 
                    alt="FixMyHood Logo" 
                    className="w-full h-full object-contain"
                  />
                </motion.div>
              </Link>
            </SidebarHeader>
            
            <SidebarContent className="p-3">
              {/* Estate switcher — only visible to estate managers */}
              {user?.user_role === 'estate_manager' && <EstateSwitcher />}

              <SidebarGroup>
                <SidebarGroupLabel className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-3 py-2">
                  Navigation
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {navigationItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton 
                          asChild 
                          className={`group rounded-[24px] md:rounded-[40px] mb-1 transition-all duration-200 ${
                            location.pathname === item.url 
                              ? 'bg-gradient-to-r from-green-600 to-green-600 text-white shadow-lg shadow-green-500/30 font-semibold' 
                              : 'hover:bg-green-50 dark:hover:bg-slate-800 hover:text-green-700 dark:hover:text-green-300'
                          }`}
                        >
                          <Link to={item.url} className="flex items-center gap-3 px-4 py-2">
                            <item.icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                              location.pathname === item.url ? '' : 'opacity-70'
                            }`} />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              {!['estate_manager', 'contractor'].includes(user?.user_role) && (
                <SidebarGroup className="mt-4">
                  <SidebarGroupLabel className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-3 py-2">
                    Community Impact
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="px-3 py-4 mx-2 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 dark:from-teal-950 dark:via-emerald-950 dark:to-cyan-950 rounded-2xl border border-teal-100 dark:border-teal-900"
                    >
                      <div className="text-center">
                        <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                          Together
                        </p>
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 font-medium">
                          We Fix Our Hood
                        </p>
                      </div>
                    </motion.div>
                  </SidebarGroupContent>
                </SidebarGroup>
              )}
            </SidebarContent>

            <SidebarFooter className="border-t border-teal-100/50 dark:border-slate-800 p-4">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <motion.button 
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center gap-3 w-full hover:bg-green-50 dark:hover:bg-slate-800 rounded-xl p-3 transition-all duration-200"
                      aria-label="User menu"
                    >
                      <Avatar className="w-10 h-10 border-2 border-green-200 dark:border-green-800 ring-2 ring-teal-100/50 dark:ring-teal-900/50">
                        <AvatarImage src={user.profile_photo} alt={user.full_name || user.email} />
                        <AvatarFallback className="bg-gradient-to-br from-green-600 to-green-600 text-white font-bold">
                          {user.full_name?.[0] || user.email?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="font-semibold text-slate-900 dark:text-white text-sm truncate">
                          {user.full_name || 'User'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate capitalize">
                          {user.user_role?.replace('_', ' ') || 'Resident'}
                        </p>
                      </div>
                    </motion.button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-2xl border-teal-100 dark:border-slate-800">
                    <DropdownMenuLabel className="font-semibold">My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-teal-100 dark:bg-slate-800" />
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("Profile"))} className="rounded-xl cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    {!['estate_manager', 'contractor'].includes(user?.user_role) && (
                      <DropdownMenuItem onClick={() => navigate(createPageUrl("MyActivity"))} className="rounded-xl cursor-pointer">
                        <Activity className="w-4 h-4 mr-2" />
                        My Activity
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("Settings"))} className="rounded-xl cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-teal-100 dark:bg-slate-800" />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400 rounded-xl cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  onClick={() => navigate('/Login')} 
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-teal-700 hover:to-emerald-700 text-white shadow-lg shadow-green-500/30 rounded-xl font-semibold"
                >
                  Login
                </Button>
              )}
            </SidebarFooter>
          </Sidebar>
            </div>

          <main className="flex-1 flex flex-col overflow-hidden">
            <header className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-b border-teal-100/50 dark:border-slate-800 px-4 py-3 md:hidden print:hidden sticky top-0 z-10 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <SidebarTrigger className="hover:bg-green-50 dark:hover:bg-slate-800 p-3 -m-1 rounded-xl transition-all">
                    <Menu className="w-5 h-5" />
                  </SidebarTrigger>
                  <h1 className="text-lg font-bold text-slate-900 dark:text-white">FixMyHood</h1>
                </div>
                {user && (
                  <div className="flex items-center">
                    {/* Co-manager avatars — overlapping, sitting just left of the profile icon */}
                    {user.user_role === 'estate_manager' && mobilePresence.length > 0 && (
                      <div className="flex -space-x-2 mr-[-6px]">
                        {mobilePresence.slice(0, 3).map((person) => (
                          <div
                            key={person.user_id}
                            className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-slate-900 overflow-hidden bg-emerald-100 flex-shrink-0 flex items-center justify-center"
                            title={person.full_name}
                          >
                            {person.profile_photo ? (
                              <img src={person.profile_photo} alt={person.full_name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              <span className="text-[10px] font-semibold text-emerald-700">
                                {(person.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <Avatar className="w-9 h-9 border-2 border-green-200 dark:border-green-800 relative z-10">
                      <AvatarImage src={user.profile_photo} alt={user.full_name || user.email} />
                      <AvatarFallback className="bg-gradient-to-br from-green-600 to-green-600 text-white text-xs font-bold">
                        {user.full_name?.[0] || user.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                )}
              </div>
            </header>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex-1 overflow-auto"
            >
              {children}
            </motion.div>
          </main>
        </div>
      </SidebarProvider>
      )}
    </>
  );
}